import cadenceGqlCall from '../utils/cadence-gql-call';
import {
  CADENCE_QUERY,
  CREATE_CADENCE_MUTATION,
  DELETE_CADENCE_EVENTS_MUTATION,
  UPDATE_CADENCE_MUTATION,
  DELETE_CADENCE_MUTATION,
  ENROLL_MEMBER_MUTATION,
  CREATE_AUTO_REMOVAL_RULE_MUTATION,
  UPDATE_AUTO_REMOVAL_RULE_MUTATION,
  DELETE_AUTO_REMOVAL_RULE_MUTATION,
  CREATE_SUCCESS_METRIC_MUTATION,
  UPDATE_SUCCESS_METRIC_MUTATION,
  DELETE_SUCCESS_METRIC_MUTATION,
  REMOVE_CADENCE_MEMBER_MUTATION,
  SAVE_CADENCE_AS_TEMPLATE_MUTATION,
  CREATE_CADENCE_FROM_TEMPLATE_MUTATION,
  SHARE_CADENCE_MUTATION,
  REVOKE_CADENCE_SHARING_MUTATION,
  UPDATE_CADENCE_SHARING_MUTATION,
  CADENCE_SHARING_SETTINGS_QUERY,
  UPDATE_CADENCE_EVENT_MUTATION,
  BULK_ENROLL_MEMBERS_MUTATION,
  CREATE_CADENCE_LIST_AUDIENCE_MUTATION,
  CREATE_CADENCE_AUDIENCE_MUTATION,
  RESYNC_CADENCE_AUDIENCE_MUTATION,
  UPDATE_CADENCE_AUDIENCE_MUTATION,
  SAVE_INDIVIDUAL_CADENCE_AUDIENCES_MUTATION,
  DELETE_CADENCE_AUDIENCE_MUTATION,
  UPDATE_CADENCE_SENDER_CONFIG_MUTATION,
  COMPLETE_MANUAL_STEP_MUTATION,
  ACTIVATE_CADENCE_MUTATION,
  REVERT_CADENCE_TO_DRAFT_MUTATION,
  DELETED_CADENCES_QUERY,
  DUPLICATE_CADENCE_MUTATION,
  RESTORE_CADENCE_MUTATION,
  RECALCULATE_CADENCE_STATS_MUTATION,
  REEVALUATE_SUCCESS_METRICS_MUTATION,
  REFRESH_CADENCE_SAVED_SEARCHES_MUTATION,
  SKIP_CADENCE_EVENT_MUTATION,
  CADENCE_SEND_PREFERENCES_QUERY,
  UPDATE_CADENCE_SEND_PREFERENCES_MUTATION,
  CREATE_TASK_CLEANUP_JOB_MUTATION,
} from '../utils/cadence-queries';

/**
 * Provides mutation functions for cadences.
 * Each function is async and throws on error — callers manage their own loading/error state.
 */
const useCadenceMutations = () => {
  const createCadence = async (input) => {
    const data = await cadenceGqlCall(CREATE_CADENCE_MUTATION, { input });
    return data.createCadence;
  };

  const updateCadence = async (id, version, input) => {
    const data = await cadenceGqlCall(UPDATE_CADENCE_MUTATION, { id: String(id), version, input });
    return data.updateCadence;
  };

  const deleteCadenceEvents = async (cadenceId, cadenceVersion, firstEventId, lastEventId, eventIds) => {
    const data = await cadenceGqlCall(DELETE_CADENCE_EVENTS_MUTATION, {
      cadenceId: String(cadenceId),
      cadenceVersion,
      firstEventId: String(firstEventId),
      lastEventId: String(lastEventId),
      eventIds: eventIds.map(String),
    });
    return data.deleteCadenceEvents;
  };

  const deleteCadence = async (id, version) => {
    const data = await cadenceGqlCall(DELETE_CADENCE_MUTATION, { id: String(id), version });
    return data.deleteCadence;
  };

  /** Enroll a contact into a cadence. */
  const enrollMember = async (cadenceId, contactId) => {
    const data = await cadenceGqlCall(ENROLL_MEMBER_MUTATION, {
      input: { cadenceId: String(cadenceId), contactId: Number(contactId), enrollmentSource: 'manual' },
    });
    return data.enrollMember;
  };

  /** Create an auto-removal rule for a cadence. */
  const createAutoRemovalRule = async (input) => {
    const data = await cadenceGqlCall(CREATE_AUTO_REMOVAL_RULE_MUTATION, { input });
    return data.createAutoRemovalRule;
  };

  /** Create a success metric for a cadence. */
  const createSuccessMetric = async (input) => {
    const data = await cadenceGqlCall(CREATE_SUCCESS_METRIC_MUTATION, { input });
    return data.createSuccessMetric;
  };

  /** Update an existing success metric. */
  const updateSuccessMetric = async (id, cadenceId, input) => {
    const data = await cadenceGqlCall(UPDATE_SUCCESS_METRIC_MUTATION, {
      id: String(id),
      cadenceId: String(cadenceId),
      input,
    });
    return data.updateSuccessMetric;
  };

  /** Delete a success metric. */
  const deleteSuccessMetric = async (id, cadenceId) => {
    const data = await cadenceGqlCall(DELETE_SUCCESS_METRIC_MUTATION, {
      id: String(id),
      cadenceId: String(cadenceId),
    });
    return data.deleteSuccessMetric;
  };

  /** Update an existing auto-removal rule. */
  const updateAutoRemovalRule = async (id, cadenceId, input) => {
    const data = await cadenceGqlCall(UPDATE_AUTO_REMOVAL_RULE_MUTATION, {
      id: String(id),
      cadenceId: String(cadenceId),
      input,
    });
    return data.updateAutoRemovalRule;
  };

  /** Delete an auto-removal rule. */
  const deleteAutoRemovalRule = async (id, cadenceId) => {
    const data = await cadenceGqlCall(DELETE_AUTO_REMOVAL_RULE_MUTATION, {
      id: String(id),
      cadenceId: String(cadenceId),
    });
    return data.deleteAutoRemovalRule;
  };

  /** Remove a member from a cadence with reason tracking. */
  const removeCadenceMember = async (input) => {
    const data = await cadenceGqlCall(REMOVE_CADENCE_MEMBER_MUTATION, { input });
    return data.removeCadenceMember;
  };

  /**
   * Duplicate a cadence: fetch it, create a copy, then selectively copy
   * events, success metrics, auto-removal rules, tags, and sharing settings
   * based on the provided options.
   *
   * @param {string|number} cadenceId - The cadence to duplicate
   * @param {string} [newName] - Custom name for the copy (defaults to "Name (Copy)")
   * @param {Object} [options] - Which parts to include in the copy
   * @param {boolean} [options.stepSetup=true] - Copy events/steps (cadences)
   * @param {boolean} [options.messageContent=true] - Copy email event content (bulk messages)
   * @param {boolean} [options.successMetrics=true] - Copy success metrics
   * @param {boolean} [options.exitEvents=true] - Copy auto-removal rules
   * @param {boolean} [options.tags=true] - Copy tags
   * @param {boolean} [options.shareSettings=false] - Copy sharing permissions
   * @param {boolean} [options.recipientSelection=true] - Copy audience/saved search config
   * @param {boolean} [options.linkedProposals=true] - Copy linked proposals
   */
  const duplicateCadence = async (cadenceId, newName, options = {}) => {
    const opts = {
      stepSetup: true,
      messageContent: true,
      successMetrics: true,
      exitEvents: true,
      tags: true,
      shareSettings: false,
      recipientSelection: true,
      linkedProposals: true,
      ...options,
    };

    const input = {
      name: newName || undefined,
      copyEvents: opts.messageContent !== false && opts.stepSetup !== false,
      copySuccessMetrics: opts.successMetrics !== false,
      copyAutoRemovalRules: opts.exitEvents !== false,
      copySendPreferences: true,
      copySharing: opts.shareSettings === true,
      copyRecipients: opts.recipientSelection !== false,
      copyProposals: opts.linkedProposals !== false,
      copyTags: opts.tags !== false,
    };

    // If caller wants to override is_template
    if (opts.isTemplate !== undefined) {
      input.isTemplate = opts.isTemplate;
    }

    const result = await cadenceGqlCall(DUPLICATE_CADENCE_MUTATION, {
      cadenceId: String(cadenceId),
      input,
    });

    const newCadence = result.duplicateCadence?.cadence;
    if (!newCadence) {
      throw new Error(result.duplicateCadence?.message || 'Failed to duplicate cadence');
    }

    return newCadence;
  };

  /**
   * Save an existing cadence as a blueprint (template).
   * Backend handles all child entity copying (events, edges, metrics, rules, proposals, send prefs).
   */
  const saveCadenceAsTemplate = async (cadenceId, input) => {
    const data = await cadenceGqlCall(SAVE_CADENCE_AS_TEMPLATE_MUTATION, {
      cadenceId: String(cadenceId),
      input,
    });
    return data.saveCadenceAsTemplate;
  };

  /**
   * Create a new cadence from a blueprint (template).
   * Backend handles all child entity copying (events, edges, metrics, rules, proposals, send prefs).
   */
  const createCadenceFromTemplate = async (templateId, input) => {
    const data = await cadenceGqlCall(CREATE_CADENCE_FROM_TEMPLATE_MUTATION, {
      templateId: String(templateId),
      input,
    });
    return data.createCadenceFromTemplate;
  };

  /** Update a single event within a cadence. Returns updated event and new cadence version. */
  const updateCadenceEvent = async (cadenceId, eventId, cadenceVersion, input) => {
    const data = await cadenceGqlCall(UPDATE_CADENCE_EVENT_MUTATION, {
      cadenceId: String(cadenceId),
      eventId: String(eventId),
      cadenceVersion,
      input,
    });
    return data.updateCadenceEvent;
  };

  /** Share a cadence with a user (set permissions). */
  const shareCadence = async (input) => {
    const data = await cadenceGqlCall(SHARE_CADENCE_MUTATION, { input });
    return data.shareCadence;
  };

  /** Update existing sharing permissions. */
  const updateCadenceSharing = async (id, cadenceId, cadenceVersion, input) => {
    const data = await cadenceGqlCall(UPDATE_CADENCE_SHARING_MUTATION, {
      id: String(id),
      cadenceId: String(cadenceId),
      cadenceVersion,
      input,
    });
    return data.updateCadenceSharing;
  };

  /** Revoke sharing access for a user. */
  const revokeCadenceSharing = async (id, cadenceId, cadenceVersion) => {
    const data = await cadenceGqlCall(REVOKE_CADENCE_SHARING_MUTATION, {
      id: String(id),
      cadenceId: String(cadenceId),
      cadenceVersion,
    });
    return data.revokeCadenceSharing;
  };

  /** Fetch current sharing settings for a cadence. */
  const fetchCadenceSharingSettings = async (cadenceId) => {
    const data = await cadenceGqlCall(CADENCE_SHARING_SETTINGS_QUERY, { cadenceId: String(cadenceId) });
    return data.cadenceSharingSettings || [];
  };

  /** Bulk enroll multiple contacts into a cadence. Returns job info. */
  const bulkEnrollMembers = async (cadenceId, contactIds) => {
    const data = await cadenceGqlCall(BULK_ENROLL_MEMBERS_MUTATION, {
      input: { cadenceId: String(cadenceId), contactIds: contactIds.map(Number) },
    });
    return data.bulkEnrollMembers;
  };

  /** Create an audience from a contact list. */
  const createCadenceListAudience = async (cadenceId, contactListId) => {
    const data = await cadenceGqlCall(CREATE_CADENCE_LIST_AUDIENCE_MUTATION, {
      input: { cadenceId: String(cadenceId), contactListId: Number(contactListId) },
    });
    return data.createCadenceListAudience;
  };

  /** Create an audience from a saved search.
   *
   * `savedSearchListId` (the contacts-system list id) is optional but should
   * be passed whenever the picker has it — the cadences saved-search refresh
   * workflow uses it to re-evaluate the underlying list before metric eval.
   */
  const createCadenceAudience = async (cadenceId, savedSearchTagId, savedSearchListId = null) => {
    const input = { cadenceId: String(cadenceId), savedSearchTagId: String(savedSearchTagId) };
    if (savedSearchListId != null) {
      input.savedSearchListId = Number(savedSearchListId);
    }
    const data = await cadenceGqlCall(CREATE_CADENCE_AUDIENCE_MUTATION, { input });
    return data.createCadenceAudience;
  };

  /** Re-trigger import on an existing audience. */
  const resyncCadenceAudience = async (audienceId) => {
    const data = await cadenceGqlCall(RESYNC_CADENCE_AUDIENCE_MUTATION, {
      audienceId: String(audienceId),
    });
    return data.resyncCadenceAudience;
  };

  /** Update audience settings (sync config, auto-add, etc.). */
  const updateCadenceAudience = async (audienceId, input) => {
    const data = await cadenceGqlCall(UPDATE_CADENCE_AUDIENCE_MUTATION, {
      audienceId: String(audienceId),
      input,
    });
    return data.updateCadenceAudience;
  };

  /** Save individually-selected contact IDs as audience rows for draft persistence.
   *  Replaces all existing individual rows for the cadence atomically. */
  const saveIndividualCadenceAudiences = async (cadenceId, contactIds) => {
    const data = await cadenceGqlCall(SAVE_INDIVIDUAL_CADENCE_AUDIENCES_MUTATION, {
      cadenceId: String(cadenceId),
      contactIds: contactIds.map(Number),
    });
    return data.saveIndividualCadenceAudiences;
  };

  const deleteCadenceAudience = async (audienceId) => {
    const data = await cadenceGqlCall(DELETE_CADENCE_AUDIENCE_MUTATION, {
      audienceId: String(audienceId),
    });
    return data.deleteCadenceAudience;
  };

  const updateCadenceSenderConfig = async (cadenceId, cadenceVersion, fromEmail, fromName, replyToEmails) => {
    const data = await cadenceGqlCall(UPDATE_CADENCE_SENDER_CONFIG_MUTATION, {
      cadenceId: String(cadenceId),
      cadenceVersion: Number(cadenceVersion),
      fromEmail: fromEmail || null,
      fromName: fromName || null,
      replyToEmails: replyToEmails || null,
    });
    return data.updateCadenceSenderConfig;
  };

  /** Activate a cadence: transitions draft → active.
   *  Pass `version` when it is already known (e.g. from a prior mutation result)
   *  to avoid an extra round-trip. Omit it only as a last resort — the backend's
   *  optimistic lock will reject stale versions, so a re-fetch is still a TOCTOU. */
  const activateCadence = async (id, version) => {
    let activateVersion = version;
    if (activateVersion == null) {
      // Fallback: re-fetch to get current version when caller doesn't have it.
      // NOTE: this is a fetch-then-act pattern — prefer passing version explicitly.
      const fetchData = await cadenceGqlCall(CADENCE_QUERY, { id: String(id) });
      const cadence = fetchData.cadence;
      if (!cadence) throw new Error('Cadence not found');
      if (cadence.isTemplate)
        throw new Error('Cannot activate a blueprint template. Create a cadence from this template first.');
      const status = (cadence.status || '').toLowerCase();
      if (status === 'active') throw new Error('Cadence is already active.');
      if (status === 'archived')
        throw new Error('Cannot activate an archived cadence. Duplicate it as a new draft instead.');
      activateVersion = cadence.version;
    }
    const data = await cadenceGqlCall(ACTIVATE_CADENCE_MUTATION, { id: String(id), version: activateVersion });
    return data.activateCadence;
  };

  /**
   * Pull a cadence back to DRAFT. Surfaced as "Cancel Send" on bulk messages.
   *
   * `version` is required — the backend guards the update on both version and
   * status so a revert cannot silently clobber an activation that fired while
   * the user was deciding. Throws on rejection; callers show the message.
   */
  const revertCadenceToDraft = async (id, version) => {
    const data = await cadenceGqlCall(REVERT_CADENCE_TO_DRAFT_MUTATION, { id: String(id), version });
    return data.revertCadenceToDraft;
  };

  /** Signal the cadence workflow that a manual step has been completed. */
  const completeManualStep = async (cadenceMemberId, cadenceEventId) => {
    const data = await cadenceGqlCall(COMPLETE_MANUAL_STEP_MUTATION, {
      cadenceMemberId: String(cadenceMemberId),
      cadenceEventId: String(cadenceEventId),
    });
    return data.completeManualStep;
  };

  /** Fetch all soft-deleted cadences for the current org. */
  const fetchDeletedCadences = async () => {
    const data = await cadenceGqlCall(DELETED_CADENCES_QUERY);
    return data.deletedCadences || [];
  };

  /** Restore a soft-deleted cadence by clearing its deleted_at. */
  const restoreCadence = async (id) => {
    const data = await cadenceGqlCall(RESTORE_CADENCE_MUTATION, { id: String(id) });
    return data.restoreCadence;
  };

  /** Trigger backend recalculation of cadence stats. */
  const recalculateCadenceStats = async (cadenceId) => {
    const data = await cadenceGqlCall(RECALCULATE_CADENCE_STATS_MUTATION, {
      cadenceId: String(cadenceId),
    });
    return data.recalculateCadenceStats;
  };

  /** Re-evaluate success metrics for all eligible members (active + completed/removed within 30 days). */
  const reevaluateSuccessMetrics = async (cadenceId) => {
    const data = await cadenceGqlCall(REEVALUATE_SUCCESS_METRICS_MUTATION, {
      cadenceId: String(cadenceId),
    });
    return data.reevaluateCadenceSuccessMetrics;
  };

  /**
   * Trigger an on-demand refresh of every saved-search list backing this cadence
   * (audience, success metrics, removal rules, condition events). Returns immediately;
   * the contacts service takes a few minutes to actually rebuild tag membership.
   *
   * Pair with reevaluateSuccessMetrics on the Refresh Metrics button so saved-search
   * metrics see the freshest data the next time the user clicks.
   */
  const refreshCadenceSavedSearches = async (cadenceId) => {
    const data = await cadenceGqlCall(REFRESH_CADENCE_SAVED_SEARCHES_MUTATION, {
      cadenceId: String(cadenceId),
    });
    return data.refreshCadenceSavedSearches;
  };

  /** Skip the current event for a cadence member, advancing them to the next step. */
  const skipCadenceEvent = async (cadenceId, memberId) => {
    const data = await cadenceGqlCall(SKIP_CADENCE_EVENT_MUTATION, {
      cadenceId: String(cadenceId),
      memberId: String(memberId),
    });
    return data.skipCadenceEvent;
  };

  /** Fetch send preferences for a cadence (or org-level if cadenceId is null). */
  const fetchCadenceSendPreferences = async (cadenceId) => {
    const data = await cadenceGqlCall(CADENCE_SEND_PREFERENCES_QUERY, {
      cadenceId: cadenceId ? String(cadenceId) : null,
    });
    return data.cadenceSendPreferences;
  };

  /** Create or update send preferences for a cadence (upsert). */
  const updateCadenceSendPreferences = async (input, version) => {
    const data = await cadenceGqlCall(UPDATE_CADENCE_SEND_PREFERENCES_MUTATION, {
      input,
      version: version ?? null,
    });
    return data.updateCadenceSendPreferences;
  };

  /** Create a background job to clean up incomplete tasks for archived/deleted cadences. */
  const createTaskCleanupJob = async (cadenceIds) => {
    const data = await cadenceGqlCall(CREATE_TASK_CLEANUP_JOB_MUTATION, {
      cadenceIds: cadenceIds.map(String),
    });
    return data.createTaskCleanupJob;
  };

  return {
    createCadence,
    updateCadence,
    deleteCadence,
    enrollMember,
    createAutoRemovalRule,
    updateAutoRemovalRule,
    deleteAutoRemovalRule,
    removeCadenceMember,
    createSuccessMetric,
    updateSuccessMetric,
    deleteSuccessMetric,
    duplicateCadence,
    updateCadenceEvent,
    saveCadenceAsTemplate,
    createCadenceFromTemplate,
    shareCadence,
    updateCadenceSharing,
    revokeCadenceSharing,
    fetchCadenceSharingSettings,
    bulkEnrollMembers,
    createCadenceListAudience,
    createCadenceAudience,
    resyncCadenceAudience,
    updateCadenceAudience,
    saveIndividualCadenceAudiences,
    deleteCadenceAudience,
    updateCadenceSenderConfig,
    completeManualStep,
    activateCadence,
    revertCadenceToDraft,
    fetchDeletedCadences,
    restoreCadence,
    recalculateCadenceStats,
    reevaluateSuccessMetrics,
    refreshCadenceSavedSearches,
    skipCadenceEvent,
    fetchCadenceSendPreferences,
    updateCadenceSendPreferences,
    createTaskCleanupJob,
    deleteCadenceEvents,
  };
};

export default useCadenceMutations;
